<template>
  <div class="filter_holder">

    <div class="filters">
      <!-- Filter button -->
      <HeadlessMenu class="filter__menu" as="div">
        <HeadlessMenuButton class="btn btn--flat btn--n btn--icon">
          <span>Filter op categorie</span>
          <Icon name="mingcute:filter-2-line" class="btn__icon" />
        </HeadlessMenuButton>
        <HeadlessMenuItems class="filters__menu-items">
          <template
            v-for="(category, i) in options" 
            :key="category.id" 
          >
            <HeadlessMenuItem
              v-slot="{ active }"
            >
              <div
                @click="() => selectCategory(category)"
                :class="[
                  'filters__menu-item',
                  modelValue.includes(category) ? 'selected' : '',
                  active ? 'active' : ''
                ]"
              >
                <span class="filter__name">{{ category.title }}</span>
                <Icon name="ic:round-close" class="icon--md" v-if="modelValue.includes(category)"/>
              </div>
            </HeadlessMenuItem>

            <div class="divider--horizontal" v-if="i < (options?.length || 0) - 1"></div>
          </template>
        </HeadlessMenuItems>
      </HeadlessMenu>
      <div class="divider--vertical"></div>
      <button class="btn btn--flat btn--n btn--icon" @click="togglePrivate" v-if="showPrivateFilter">
        <span>{{ private ? 'Toon alle artikelen' : 'Alleen privé artikelen'}}</span>
        <Icon :name="private ? 'mingcute:unlock-line' : 'mingcute:lock-line'" class="btn__icon"/>
      </button>
    </div>
    <div class="filter_values">
      <CategoryHolder :items="tags" closeable @clicked="onClickCategory" class="filter__items"/>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type Category from '@/typings/strapi/objects/category';

const props = defineProps({
  modelValue: {
    type: Array as PropType<Array<Category>>,
    default: []
  },
  options: {
    type: Array as PropType<Array<Category>>,
    default: []
  },
  private: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  showPrivateFilter: {
    type: Boolean as PropType<boolean>,
    default: false
  }
});

const { find } = useStrapi();

const emit = defineEmits(['update:modelValue', 'update:private']);

const tags = computed(() => {
  const categories = props.modelValue.map(c => ({
    id: c.id,
    title: c.title,
    type: 'mono'
  }));

  if (props.private) {
    categories.push({
      id: 0,
      title: 'Privé',
      type: 'exclusive'
    });
  }

  return categories;
});

function selectCategory(category: Category) {
  let value = props.modelValue;

  if (props.modelValue.includes(category)) {
    value = value.filter((c) => c.id !== category.id);
  } else {
    value.push(category);
  }

  emit('update:modelValue', value);
};

function onClickCategory(id: number) {
  if (id === 0 && props.private) {
    // React to private filter
    emit('update:private', false);
    return;
  }

  emit('update:modelValue', props.modelValue.filter(
    (c) => c.id !== id
  ));
}

function togglePrivate() {
  emit('update:private', !props.private);
}
</script>

<style lang="scss">
.filter_holder {
  margin-bottom: 2rem;
  padding: 0.5rem;
}

.filter_values {
  margin-top: 1rem;
}

.filters {
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 1rem;

  .btn {
    padding: 0.5rem;
    border: 2px solid transparent;
    font-size: 0.9rem;
  }

  @include breakpoint(null, $sm) {
    flex-direction: column;
    gap: 0.5rem;
  }

  .divider--vertical {
    width: 2px;
    height: 1rem;
    background: $sixth;
    margin: 0 0.5rem;
    flex-shrink: 0;
    flex-basis: 2px;

    @include breakpoint(null, $sm) {
      width: 100%;
      height: 2px;
    }
  }
}

.filters__menu {
  position: relative;
  display: inline-block;
}

.filters__menu-items {
  position: absolute;
  // top: calc(100% + 0.5rem);
  left: 0;
  width: 300px;
  height: auto;
  max-height: 6 * 2rem;
  background: $background;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
  border-radius: 0.25rem;
  border: 2px solid $sixth;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: auto;

  @include breakpoint(null, $sm) {
    width: 100%;
  }
}

.filters__menu-item {
  padding: 0.75rem 1rem;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: $primary;
  transition: all 250ms ease-in-out;
  cursor: pointer;

  .filter__name {
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 0;
    margin-right: 0.5rem;
    text-align: left;
    color: $text;
    position: relative;
    padding-left: 0;
    transition: all 250ms ease-in-out;

    &::after {
      content: '';
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      background: $primary;
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      opacity: 0;
      transition: all 100ms ease-in-out;
      transition-delay: 0ms;
    }
  }

  &:hover, &:active, &.active {
    .filter__name {
      color: $primary;
      padding-left: 1rem;

      &::after {
        opacity: 1;
        transition-delay: 150ms;
      }
    }
  }

  &.selected {
    background: $primary;
    color: $background;

    .filter__name {
      color: $background;

      &::after {
        background: $background;
      }
    }
  }
}

.divider--horizontal {
  width: 100%;
  height: 2px;
  background: $sixth;
  margin: 0;
  flex-shrink: 0;
  display: block;
  inset: 0 1rem;
}

.filter__items {
  display: flex;

  @include breakpoint(null, $sm) {
    justify-content: center;
    margin-top: 0.75rem;
  }
}
</style>
